// Components
import {
  Alert,
  NumberInput,
  Select,
  Stack,
  Textarea,
  Skeleton,
} from "@mantine/core";
import { IconInfoCircle } from "@tabler/icons-react";
import { FormActions } from "@benchmark/ui/components/atomic";
import { TrimmedTextInput as TextInput } from "@benchmark/ui/components/atomic";

// Constants
import { OBSERVATION_TYPE_SELECT_OPTIONS } from "@benchmark/core/constants";

// Hooks
import { useForm } from "@mantine/form";
import { useMedplum, useResource } from "@medplum/react";
import { useCallback, useEffect } from "react";
import { useNavigate } from "react-router";
import { useFormSubmission } from "@benchmark/ui/hooks";
import { useFormProtection } from "@benchmark/ui/hooks";
import { useStableParams } from "@/hooks/common/useStableParams";
import { usePatientResource } from "@/hooks/shared/useFHIRResource";
import { useCurrentPractitionerRole } from "@/hooks/shared/useCurrentPractitionerRole";

// Types
import type { Observation, Patient } from "@medplum/fhirtypes";
import type { ObservationFormData } from "@benchmark/core/types";

// Utils
import { observationToForm } from "@benchmark/core/adapters";
import {
  showErrorNotification,
  showSuccessNotification,
} from "@benchmark/ui/utils";
import { createObservationFacade } from "@/facades/observation.facade";

/**
 * FHIR R4-compliant form for Observation resources.
 * @agentic connections
 */

/** Converts ISO string to datetime-local input format. */
const formatDateTimeForInput = (isoString: string): string => {
  // Convert ISO string to local datetime-local format
  const date = new Date(isoString);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/** Converts datetime-local input to ISO string. */
const formatInputForDateTime = (inputValue: string): string => {
  // Convert datetime-local input to ISO string
  const localDate = new Date(inputValue);
  return localDate.toISOString();
};

/**
 * Props for the ObservationForm component.
 * @agentic connections
 */
type ObservationFormProps = {
  patientId?: string;
  observation?: Observation;
  onSuccess?: (observationId: string) => void;
  onCancel?: () => void;
};

/**
 * Form for creating and editing FHIR Observation (assessment) resources.
 * @agentic connections
 */
export const ObservationForm = ({
  patientId: propPatientId,
  observation: propObservation,
  onSuccess,
  onCancel,
}: ObservationFormProps = {}) => {
  const navigate = useNavigate();
  const medplum = useMedplum();
  const observationFacade = createObservationFacade(medplum);
  const { practitionerRole } = useCurrentPractitionerRole();
  const { patientId: urlPatientId, observationId } = useStableParams();

  const effectivePatientId = propPatientId || urlPatientId;
  const { data: patientResource } = usePatientResource(effectivePatientId);
  const patient = patientResource as Patient | undefined;

  const fetchedObservation = useResource(
    observationId ? { reference: `Observation/${observationId}` } : undefined,
  ) as Observation | undefined;
  const existingObservation = propObservation || fetchedObservation;

  const form = useForm<ObservationFormData>({
    initialValues: {
      effective: formatDateTimeForInput(new Date().toISOString()),
    },
    validate: {
      "observation-type": (value) =>
        !value ? "Please select an observation type" : null,
      value: (v) =>
        v === undefined || v === null ? "Please enter a value" : null,
      effective: (value) =>
        !value ? "Please enter an observation date/time" : null,
    },
  });

  const { markAsClean, ConfirmationModal } = useFormProtection(form, {
    enabled: true,
    blockNavigation: true,
    confirmationMessage:
      "You have unsaved assessment changes. Are you sure you want to close?",
  });

  const { isSubmitting, createSubmissionHandler } = useFormSubmission();

  useEffect(() => {
    if (existingObservation) {
      const extractedData = observationToForm(existingObservation);
      if (extractedData.effective) {
        extractedData.effective = formatDateTimeForInput(
          extractedData.effective,
        );
      }
      form.setValues(extractedData);
      form.resetDirty();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingObservation]);

  const handleSubmit = useCallback(
    (formData: ObservationFormData) =>
      createSubmissionHandler(async (fd: ObservationFormData) => {
        if (!practitionerRole || !patient) {
          return;
        }

        const submissionData = {
          ...fd,
          effective: fd.effective ? formatInputForDateTime(fd.effective) : "",
        };

        const observation = await observationFacade.upsert({
          formData: submissionData,
          patient,
          practitionerRole,
          existingObservation,
        });

        if (!observation) {
          throw new Error("Failed to create observation");
        }

        showSuccessNotification(
          `Observation ${existingObservation ? "updated" : "created"} successfully.`,
        );

        markAsClean();

        if (onSuccess) {
          onSuccess(observation.id!);
        } else {
          navigate(
            `/Patient/${patient.id}/Observation/${observation.id}`,
          )?.catch(console.error);
        }
      })(formData),
    [
      observationFacade,
      navigate,
      patient,
      practitionerRole,
      existingObservation,
      createSubmissionHandler,
      onSuccess,
    ],
  );

  if (observationId && !existingObservation) {
    return (
      <Stack gap="md">
        <Skeleton height={32} width="40%" />
        <Skeleton height={14} width="30%" />
        <Skeleton height={36} />
        <Skeleton height={14} width="25%" />
        <Skeleton height={36} />
        <Skeleton height={44} width="200px" />
      </Stack>
    );
  }

  if (!patient || !practitionerRole) {
    return (
      <Stack gap="md">
        <Skeleton height={32} width="40%" />
        <Skeleton height={14} width="30%" />
        <Skeleton height={36} />
        <Skeleton height={14} width="25%" />
        <Skeleton height={36} />
        <Skeleton height={44} width="200px" />
      </Stack>
    );
  }

  return (
    <>
      <form
        onSubmit={form.onSubmit(handleSubmit, () => {
          showErrorNotification(
            "Please fix the form errors before submitting.",
          );
        })}
      >
        <Stack gap="md">
          {!existingObservation && (
            <Alert
              variant="light"
              color="blue"
              icon={<IconInfoCircle size={16} />}
            >
              This records a single assessment score only. To capture a full
              questionnaire with individual responses, send a survey link or
              complete the assessment within an encounter.
            </Alert>
          )}

          <Select
            label="Assessment Type"
            placeholder="Select assessment type"
            data={OBSERVATION_TYPE_SELECT_OPTIONS}
            required
            searchable
            clearable
            disabled={!!existingObservation}
            {...form.getInputProps("observation-type")}
          />

          <TextInput
            label="Assessment Date/Time"
            type="datetime-local"
            required
            disabled={!!existingObservation}
            value={
              form.values.effective
                ? formatDateTimeForInput(form.values.effective)
                : ""
            }
            onChange={(e) => {
              const inputValue = e.target.value;
              if (inputValue) {
                form.setFieldValue(
                  "effective",
                  formatInputForDateTime(inputValue),
                );
              } else {
                form.setFieldValue("effective", "");
              }
            }}
            error={form.errors.effective}
          />

          <NumberInput
            label="Value"
            placeholder="Enter score value"
            required
            step={1}
            min={0}
            allowNegative={false}
            hideControls
            value={form.values.value}
            onChange={(value) => {
              form.setFieldValue(
                "value",
                typeof value === "number" ? Math.round(value) : undefined,
              );
            }}
            error={form.errors.value}
          />

          <Textarea
            label="Notes"
            placeholder="Optional notes about this observation"
            rows={3}
            {...form.getInputProps("note")}
          />

          <FormActions
            isSubmitting={isSubmitting}
            resourceType="Observation"
            isEditing={!!existingObservation}
            showCancel={true}
            onCancel={onCancel}
          />
        </Stack>
      </form>
      <ConfirmationModal />
    </>
  );
};
